import { mod10, mod11Rest } from "./boleto";
import { isIsoDate } from "./dates";
import { DomainError } from "./errors";
import { isUf, type Uf } from "./dashboard";

// ===== GNRE e DARE-SP (T2.11, RF-VEN-11) =====
//
// Guias de recolhimento do ICMS devido a outra UF pela NF-e autorizada (05 §8 passo 5): ICMS-ST, DIFAL (EC 87/2015) e
// FCP, uma guia por UF favorecida e receita. Sem provedor (a Nuvem Fiscal não emite GNRE), o ERP gera os dados, o XML
// do lote do Portal GNRE (leiaute 2.00) e confere o código de barras de arrecadação que o portal devolve.

export type GnreKind = "gnre" | "dare_sp";
export type GnreRevenue = "st" | "difal" | "fcp";
export const GNRE_REVENUES: readonly GnreRevenue[] = ["st", "difal", "fcp"];

/**
 * Receitas "por operação" do Portal GNRE: 100099 ICMS-ST por operação; 100102 ICMS consumidor final não contribuinte
 * outra UF por operação (DIFAL); 100129 ICMS Fundo Estadual de Combate à Pobreza por operação.
 */
export const GNRE_REVENUE_CODE: Readonly<Record<GnreRevenue, string>> = {
  st: "100099",
  difal: "100102",
  fcp: "100129",
};
/** DARE-SP: 063-2 (ICMS — recolhimentos especiais). Default para confirmar com o contador. */
export const DARE_SP_REVENUE_CODE = "063-2";
export const GNRE_LOTE_MAX_GUIDES = 50;
/** Teto de uma guia (o valor vai em 11 posições no código de barras: R$ 999.999.999,99). */
export const GNRE_MAX_CENTS = 99_999_999_999;

export type GnreSpGuide = "gnre" | "dare_sp";

export type GnrePlanInput = {
  model: string;
  direction: "out" | "in";
  /** finNFe (4 = devolução). */
  purpose: number;
  status: string;
  companyUf: Uf;
  /** UF do destinatário ("EX" no exterior). */
  destUf: string | null;
  /** idDest. */
  destination: number;
  totals: { stCents: number; fcpStCents: number; difalCents: number; difalFcpCents: number };
  /** IE de substituto tributário da empresa por UF (recolhe por apuração, sem guia por operação). */
  stRegistrations: Partial<Record<string, string>>;
  spGuide: GnreSpGuide;
  /** Data de emissão (AAAA-MM-DD, São Paulo). */
  issueDate: string;
};

export type GnreCandidate = {
  kind: GnreKind;
  uf: Uf;
  revenue: GnreRevenue;
  revenueCode: string;
  amountCents: number;
  dueDate: string;
  /** AAAA-MM. */
  referencePeriod: string;
};

export type GnreBlockerCode =
  "invoice_not_authorized" | "not_outbound" | "not_interstate" | "st_registered" | "nothing_due";
export type GnrePlan = {
  candidates: GnreCandidate[];
  blockers: { code: GnreBlockerCode; message: string }[];
};

const fail = (code: string, message: string): never => {
  throw new DomainError(code, message);
};

const amount = (v: number, what: string) => {
  if (!Number.isSafeInteger(v) || v < 0 || v > GNRE_MAX_CENTS)
    fail("invalid_amount", `${what}: valor inválido.`);
  return v;
};

/**
 * Guias que a NF-e pede (só leitura; quem grava é o DO). Uma por UF × receita: ST = vST; DIFAL = vICMSUFDest; FCP =
 * vFCPUFDest + vFCPST (mesma receita 100129). Nenhuma quando a nota não é saída normal autorizada, a operação não é
 * interestadual ou a empresa tem IE de substituto na UF do destino — `blockers` explica por quê.
 */
export function planGnreGuides(i: GnrePlanInput): GnrePlan {
  const st = amount(i.totals.stCents, "ICMS-ST");
  const difal = amount(i.totals.difalCents, "DIFAL");
  const fcp = amount(i.totals.fcpStCents, "FCP-ST") + amount(i.totals.difalFcpCents, "FCP do DIFAL");
  if (!isIsoDate(i.issueDate)) fail("invalid_date", "Data de emissão inválida.");
  const none = (code: GnreBlockerCode, message: string): GnrePlan => ({
    candidates: [],
    blockers: [{ code, message }],
  });
  if (i.status !== "authorized") return none("invoice_not_authorized", "Guias só de NF-e autorizada.");
  if (i.model !== "55" || i.direction !== "out" || i.purpose === 4)
    return none("not_outbound", "Guias só de NF-e de saída (devolução e entrada não geram guia).");
  const uf = i.destUf ?? "";
  if (i.destination !== 2 || !isUf(uf) || uf === i.companyUf)
    return none(
      "not_interstate",
      "Operação interna ou com o exterior: o ICMS é apurado na própria UF, sem guia.",
    );
  if (i.stRegistrations[uf])
    return none(
      "st_registered",
      `A empresa tem inscrição estadual de substituto tributário em ${uf}: o imposto é recolhido por apuração, sem guia por operação.`,
    );
  const kind: GnreKind = uf === "SP" && i.spGuide === "dare_sp" ? "dare_sp" : "gnre";
  const parts: [GnreRevenue, number][] = [
    ["st", st],
    ["difal", difal],
    ["fcp", fcp],
  ];
  const candidates = parts
    .filter(([, v]) => v > 0)
    .map(([revenue, v]) => {
      if (v > GNRE_MAX_CENTS) fail("invalid_amount", "Valor da guia acima do teto.");
      return {
        kind,
        uf,
        revenue,
        revenueCode: kind === "dare_sp" ? DARE_SP_REVENUE_CODE : GNRE_REVENUE_CODE[revenue],
        amountCents: v,
        dueDate: i.issueDate,
        referencePeriod: i.issueDate.slice(0, 7),
      };
    });
  return candidates.length
    ? { candidates, blockers: [] }
    : none("nothing_due", "A NF-e não tem ICMS-ST, DIFAL nem FCP para outra UF.");
}

// ---------- código de barras de arrecadação (Febraban: concessionárias e órgãos governamentais) ----------
//
// 44 posições: 1 = "8" (arrecadação), 2 = segmento, 3 = identificador do valor (6/7 = DV mód. 10; 8/9 = DV mód. 11;
// 6/8 = valor efetivo em reais, 7/9 = valor de referência), 4 = DV geral, 5–15 = valor, 16–44 = empresa/órgão e campo
// livre. Linha digitável: 4 blocos de 11 posições, cada um seguido do próprio DV no mesmo módulo (48 posições).

const invalidBarcode = (msg: string): never => fail("invalid_barcode", msg);

const isMod10 = (valueId: string) => valueId === "6" || valueId === "7";

/** DV de arrecadação: mód. 10 (6/7) ou mód. 11 com pesos 2–9 (8/9; resto 0 ou 1 → 0, resto 10 → 1). */
function collectionDv(valueId: string, s: string): number {
  if (isMod10(valueId)) return mod10(s);
  const r = mod11Rest(s);
  return r < 2 ? 0 : 11 - r;
}

export type CollectionBarcodeParts = {
  segment: string;
  valueId: "6" | "7" | "8" | "9";
  amountCents: number;
  /** Posições 16–44 (29 dígitos). */
  rest: string;
};

/** Monta o código de 44 posições (testes e simulação). */
export function buildCollectionBarcode(p: CollectionBarcodeParts): string {
  if (!/^\d$/.test(p.segment)) invalidBarcode("Segmento inválido.");
  if (!/^\d{29}$/.test(p.rest)) invalidBarcode("Campo livre: 29 dígitos.");
  if (!Number.isSafeInteger(p.amountCents) || p.amountCents < 0 || p.amountCents > GNRE_MAX_CENTS)
    invalidBarcode("Valor inválido.");
  const body = `8${p.segment}${p.valueId}`;
  const tail = `${String(p.amountCents).padStart(11, "0")}${p.rest}`;
  const dv = collectionDv(p.valueId, body + tail);
  return `${body}${dv}${tail}`;
}

/** Código de 44 → linha digitável de 48 (DV por bloco). */
export function collectionBarcodeToLine(barcode: string): string {
  const valueId = barcode[2] ?? "";
  let line = "";
  for (let k = 0; k < 4; k++) {
    const block = barcode.slice(k * 11, k * 11 + 11);
    line += block + String(collectionDv(valueId, block));
  }
  return line;
}

export type ParsedCollectionBarcode = {
  barcode: string;
  digitableLine: string;
  /** Valor em centavos quando o 3º dígito é 6 ou 8; null = valor de referência. */
  amountCents: number | null;
};

/**
 * Lê o código de barras (44) ou a linha digitável (48) de uma guia de arrecadação, aceitando pontos, espaços e hífens.
 * Confere o produto (8), o identificador do valor, os DVs dos blocos e o DV geral.
 */
export function parseCollectionBarcode(input: string): ParsedCollectionBarcode {
  const s = input.replace(/[\s.-]/g, "");
  if (!/^\d+$/.test(s) || (s.length !== 44 && s.length !== 48))
    invalidBarcode("Informe o código de barras (44 dígitos) ou a linha digitável (48 dígitos) da guia.");
  if (s[0] !== "8") invalidBarcode("Não é código de arrecadação (guia começa com 8; isto parece um boleto).");
  const valueId = s[2]!;
  if (!"6789".includes(valueId)) invalidBarcode("Identificador de valor inválido (3º dígito).");
  let barcode = s;
  if (s.length === 48) {
    barcode = "";
    for (let k = 0; k < 4; k++) {
      const block = s.slice(k * 12, k * 12 + 11);
      if (collectionDv(valueId, block) !== Number(s[k * 12 + 11]))
        invalidBarcode(`Dígito verificador do bloco ${k + 1} da linha digitável não confere.`);
      barcode += block;
    }
  }
  if (collectionDv(valueId, barcode.slice(0, 3) + barcode.slice(4)) !== Number(barcode[3]))
    invalidBarcode("Dígito verificador geral do código de barras não confere.");
  return {
    barcode,
    digitableLine: collectionBarcodeToLine(barcode),
    amountCents: valueId === "6" || valueId === "8" ? Number(barcode.slice(4, 15)) : null,
  };
}

/** Segmento Febraban das guias de tributo (GNRE e DARE-SP): 5 = órgãos governamentais. */
export const GNRE_BARCODE_SEGMENT = "5";

/**
 * Confere o código devolvido pelo portal contra a guia (revisão de segurança T2.11): DVs (parseCollectionBarcode),
 * segmento de órgão governamental (conta de luz, água, telefone ou carnê com o mesmo valor não "emite" a guia) e valor
 * efetivo em reais (3º dígito 6 ou 8) igual ao da guia — valor de referência (7/9) não permitiria conferir o valor.
 */
export function checkGnreBarcode(input: string, amountCents: number): ParsedCollectionBarcode {
  const parsed = parseCollectionBarcode(input);
  if (parsed.barcode[1] !== GNRE_BARCODE_SEGMENT)
    invalidBarcode("Não é guia de tributo: o 2º dígito (segmento) de GNRE/DARE é 5 (órgãos governamentais).");
  if (parsed.amountCents === null || parsed.amountCents !== amountCents)
    fail(
      "barcode_amount_mismatch",
      "O valor do código de barras é diferente do valor da guia. Confira a guia emitida no portal.",
    );
  return parsed;
}

/** "84670000001-7 43590024020-9 …" (4 blocos). */
export function formatCollectionLine(line: string): string {
  return [0, 1, 2, 3].map((k) => `${line.slice(k * 12, k * 12 + 11)}-${line[k * 12 + 11]}`).join(" ");
}

// ---------- XML do lote (Portal GNRE, leiaute 2.00) ----------

export type GnreEmitter = {
  cnpj: string;
  legalName: string;
  address: string | null;
  /** IBGE com 7 dígitos (o XML leva os 5 sem a UF). */
  cityIbge: string | null;
  uf: string;
  cep: string | null;
  phone: string | null;
};

export type GnreLoteGuide = {
  uf: string;
  revenue: GnreRevenue;
  revenueCode: string;
  amountCents: number;
  dueDate: string;
  referencePeriod: string;
  invoiceNumber: number;
  accessKey: string;
  recipient: { document: string; ie: string | null; name: string; cityIbge: string | null } | null;
};

const TYPOGRAPHIC: readonly [RegExp, string][] = [
  [/[‘’‚′]/g, "'"],
  [/[“”„″]/g, '"'],
  [/[–—−]/g, "-"],
  [/…/g, "..."],
  [/\u00a0/g, " "],
];

/** Texto para o XML: sem controles, pontuação tipográfica em ASCII, espaços colapsados, cortado e escapado. */
export function gnreXmlText(s: string, max: number): string {
  let t = s;
  for (const [re, to] of TYPOGRAPHIC) t = t.replace(re, to);
  t = t
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f-\u009f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, max);
  return t.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

const money = (cents: number) => `${Math.trunc(cents / 100)}.${String(cents % 100).padStart(2, "0")}`;
const digitsOf = (s: string | null | undefined) => (s ?? "").replace(/\D/g, "");

function identification(doc: string, ie: string | null) {
  const d = digitsOf(doc);
  const tag = d.length === 11 ? "CPF" : "CNPJ";
  // IE sem dígitos ("ISENTO") não vai: `<IE></IE>` vazio é recusado pelo leiaute.
  const ieDigits = digitsOf(ie);
  return `<identificacao><${tag}>${d}</${tag}>${ieDigits ? `<IE>${ieDigits}</IE>` : ""}</identificacao>`;
}

/**
 * Lote GNRE 2.00 (`TLote_GNRE`), uma `TDadosGNRE` simples (tipoGnre 0) por guia: emitente, item com receita,
 * documento de origem (tipo 10 = número da NF-e), referência, vencimento, valor principal (tipo 11 = ICMS, 12 = FCP) e
 * destinatário; `valorGNRE` e `dataPagamento` = vencimento. Regras por UF do portal (detalhamento, produto, campos
 * extras) não entram: o portal recusa o que a UF não aceita.
 */
export function buildGnreLoteXml(emitter: GnreEmitter, guides: readonly GnreLoteGuide[]): string {
  if (guides.length === 0 || guides.length > GNRE_LOTE_MAX_GUIDES)
    fail("validation", `Escolha de 1 a ${GNRE_LOTE_MAX_GUIDES} guias para o lote.`);
  const cnpj = digitsOf(emitter.cnpj);
  const city = digitsOf(emitter.cityIbge);
  if (cnpj.length !== 14 || !emitter.legalName.trim() || !isUf(emitter.uf) || city.length !== 7)
    fail(
      "gnre_emitter_incomplete",
      "Complete o cadastro fiscal da empresa (CNPJ, razão social, UF e município com código IBGE) antes do lote.",
    );
  const cep = digitsOf(emitter.cep);
  const phone = digitsOf(emitter.phone);
  const emit =
    `<contribuinteEmitente>${identification(cnpj, null)}` +
    `<razaoSocial>${gnreXmlText(emitter.legalName, 60)}</razaoSocial>` +
    (emitter.address ? `<endereco>${gnreXmlText(emitter.address, 60)}</endereco>` : "") +
    `<municipio>${city.slice(2)}</municipio><uf>${emitter.uf}</uf>` +
    (cep.length === 8 ? `<cep>${cep}</cep>` : "") +
    (phone.length >= 6 && phone.length <= 11 ? `<telefone>${phone}</telefone>` : "") +
    `</contribuinteEmitente>`;
  const body = guides
    .map((g) => {
      if (!Number.isSafeInteger(g.amountCents) || g.amountCents <= 0 || g.amountCents > GNRE_MAX_CENTS)
        fail("invalid_amount", "Valor da guia inválido.");
      if (!isUf(g.uf) || !isIsoDate(g.dueDate) || !/^\d{4}-\d{2}$/.test(g.referencePeriod))
        fail("validation", "Guia com UF, vencimento ou referência inválidos.");
      const [year, month] = g.referencePeriod.split("-");
      const r = g.recipient;
      const recipient =
        r && digitsOf(r.document)
          ? `<contribuinteDestinatario>${identification(r.document, r.ie)}` +
            `<razaoSocial>${gnreXmlText(r.name, 60)}</razaoSocial>` +
            (digitsOf(r.cityIbge).length === 7
              ? `<municipio>${digitsOf(r.cityIbge).slice(2)}</municipio>`
              : "") +
            `</contribuinteDestinatario>`
          : "";
      const value = money(g.amountCents);
      return (
        `<TDadosGNRE versao="2.00"><ufFavorecida>${g.uf}</ufFavorecida><tipoGnre>0</tipoGnre>${emit}` +
        `<itensGNRE><item><receita>${digitsOf(g.revenueCode)}</receita>` +
        `<documentoOrigem tipo="10">${g.invoiceNumber}</documentoOrigem>` +
        `<referencia><periodo>0</periodo><mes>${month}</mes><ano>${year}</ano></referencia>` +
        `<dataVencimento>${g.dueDate}</dataVencimento>` +
        `<valor tipo="${g.revenue === "fcp" ? "12" : "11"}">${value}</valor>` +
        `${recipient}</item></itensGNRE>` +
        `<valorGNRE>${value}</valorGNRE><dataPagamento>${g.dueDate}</dataPagamento></TDadosGNRE>`
      );
    })
    .join("");
  return (
    `<?xml version="1.0" encoding="UTF-8"?>` +
    `<TLote_GNRE xmlns="http://www.gnre.pe.gov.br" versao="2.00"><guias>${body}</guias></TLote_GNRE>`
  );
}
